/**
 * scoringDimensions — which of the workspace's attributes a person can score on.
 *
 * The dimensions are the workspace's (Settings → Attributes): the built-ins under
 * the names it gives them (migration 949) and any it defined itself. Roadmap 1C
 * V-10: screens that offered a fixed list in code (the phone's scratch weighted
 * scoring and custom matrix) read them from here instead.
 */

import type { Attribute } from '@/types/prioritization/lifecycle';

/**
 * May this attribute be chosen as a criterion? A built-in or scoring attribute
 * always may; a custom field (a data-capture field such as "Launch Date") only
 * when the workspace opted it in (`usable_as_criterion`, lifecycle.ts).
 */
export function isCriterionEligible(attr: Pick<Attribute, 'isCustomField' | 'usable_as_criterion'>): boolean {
  return !attr.isCustomField || attr.usable_as_criterion === true;
}

/** Value types a 1–5 score can stand for: numbers and ordered choices, not text, dates or yes/no. */
const SCORABLE_TYPES = new Set(['number', 'scale', 'select', 'enum', 'rating']);

/** Criterion-eligible attributes whose values are numbers or ordered choices, in the workspace's order. */
export function scoringDimensions<T extends Pick<Attribute, 'key' | 'label' | 'type' | 'isCustomField' | 'usable_as_criterion'>>(
  attributes: readonly T[],
): T[] {
  return attributes.filter((a) => !!a.key && isCriterionEligible(a) && SCORABLE_TYPES.has(String(a.type)));
}

/** Dimensions where a lower score is the better one (scored the other way round in a total). */
const LOWER_IS_BETTER = new Set(['effort', 'risk', 'cost', 'cost_to_restart', 'job_duration', 'job_size']);

export function lowerIsBetter(key: string): boolean {
  return LOWER_IS_BETTER.has(key);
}
